import { useState, useEffect, useCallback } from "react";
import { useCaptureEvent } from "./useCaptureEvent";
import useUpdateQuery from "./useUpdateQuery";
import { paginationConfig } from "../axios/constant";
import { useQuery, useQueryClient } from "react-query";
import { isEmptyObject } from "../utils/GenericFn";
import { useLocation } from "react-router-dom";

const parseQueryParamsFromUrl = () => {
  const params = new URLSearchParams(window.location.search);

  const filters = {};
  const page = parseInt(params.get("page")) || 1;
  const perPage = parseInt(params.get("per_page")) || 20;
  const sort = params.get("sort") || "";

  for (const [key, value] of params.entries()) {
    const match = key.match(/^filter\[(.+)\]$/);
    if (match) {
      filters[match[1]] = {
        selectedScope: match[1],
        value: value.replace(/\|/g, ","),
      };
    }
  }

  return {
    filters,
    pagination: { page, perPage },
    sort,
    hasParams: params.toString().length > 0,
  };
};

/**
 * Custom React hook for managing table data with server-side filtering, pagination, and sorting.
 * Automatically syncs state with URL query parameters for bookmarkable/shareable table states.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.queryKey - Unique key for React Query caching
 * @param {Function} config.fetchFn - Async function to fetch table data from API
 * @param {string} [config.defaultSort=""] - Default sort column (prefix with "-" for descending)
 * @param {Object} [config.defaultFilters={}] - Default filter values in format: { key: { selectedScope: string, value: string } }
 * @param {Object} [config.defaultHeaders={}] - Default HTTP headers for API requests
 * @param {Object} [config.restObject={}] - Additional parameters to pass to fetchFn
 * @param {Function} [config.getCount] - Callback function called with data count when data changes
 * @param {boolean} [config.enabled=true] - Whether to enable automatic query execution
 * @param {Function} [config.onSuccess] - Success callback after data fetch completes
 * @param {Function} [config.onError] - Error callback after failed fetch
 * 
 * @returns {Object} Table state and control functions
 * @returns {Array} returns.data - Fetched table data array
 * @returns {Object} returns.paginationInfo - Pagination metadata (page, perPage, total, etc.)
 * @returns {Object} returns.filterMetadata - Available filter options from API
 * @returns {Object} returns.columnMetadata - Column configuration from API
 * @returns {Object} returns.apiResponse - Complete API response object
 * @returns {boolean} returns.isLoading - True during initial data load
 * @returns {boolean} returns.isFetching - True during any data fetch (including refetch)
 * @returns {boolean} returns.isFetched - True after data has been fetched at least once
 * @returns {boolean} returns.isSuccess - True when data fetch succeeded
 * @returns {boolean} returns.isError - True when data fetch failed
 * @returns {Object} returns.query - React Query object for advanced usage
 * @returns {Object} returns.filters - Current filter state
 * @returns {string} returns.sort - Current sort state (column name, "-" prefix for descending)
 * @returns {Object} returns.pagination - Current pagination state { page, perPage }
 * @returns {Object} returns.columns - Current column configuration
 * @returns {Array} returns.sortedVisibleColumns - Filtered and sorted visible columns
 * @returns {Function} returns.setFilters - Direct setter for filters (use with caution)
 * @returns {Function} returns.setSort - Direct setter for sort (use with caution)
 * @returns {Function} returns.setPagination - Direct setter for pagination (use with caution)
 * @returns {Function} returns.getFiltersObject - Update filters and reset to page 1
 * @returns {Function} returns.getPageNumber - Update current page number
 * @returns {Function} returns.handleSetSort - Toggle sort on a column (asc -> desc -> asc)
 * @returns {Function} returns.getColumns - Update column visibility and ordering
 * @returns {Function} returns.refetchData - Manually trigger data refetch
 * @returns {Function} returns.resetTable - Reset all filters, pagination, and sort to defaults
 * 
 * @example
 * // Basic usage
 * const {
 *   data,
 *   paginationInfo,
 *   isLoading,
 *   getFiltersObject,
 *   getPageNumber,
 * } = useTableData({
 *   queryKey: "members_list",
 *   fetchFn: getMembers,
 *   defaultHeaders: { "X-Workspace-Id": workspaceId },
 *   enabled: !!workspaceId,
 * });
 * 
 * @example
 * // With filters and sorting
 * const tableData = useTableData({
 *   queryKey: "companies",
 *   fetchFn: getCompanies,
 *   defaultSort: "-created_at",
 *   defaultFilters: {
 *     status: { selectedScope: "status", value: "active" }
 *   },
 *   onSuccess: (data) => console.log("Loaded", data.data.length, "items"),
 * });
 */
export const useTableData = ({
  queryKey,
  fetchFn,
  defaultSort = "",
  defaultFilters = {},
  defaultHeaders = {},
  restObject = {},
  getCount,
  enabled = true,
  onSuccess: externalSuccess,
  onError: externalError,
}) => {
  const queryClient = useQueryClient();

  const {
    filters: initialFilters,
    pagination: initialPagination,
    sort: initialSort,
    hasParams,
  } = parseQueryParamsFromUrl();

  const location = useLocation();

  const [isHydrated, setIsHydrated] = useState(!hasParams); // true if no params

  // Core data pieces
  const [data, setData] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({});
  const [filterMetadata, setFilterMetadata] = useState({});
  const [columnMetadata, setColumnMetadata] = useState({});
  const [apiResponse, setApiResponse] = useState(null);

  const [filters, setFilters] = useState(hasParams ? initialFilters : defaultFilters);
  const [pagination, setPagination] = useState(hasParams ? initialPagination : paginationConfig);
  const [sort, setSort] = useState(hasParams ? initialSort : defaultSort);

  const [filterQuery, setFilterQuery] = useState("");
  const [columns, setColumns] = useState({});
  const [sortedVisibleColumns, setSortedVisibleColumns] = useState(null);

  const captureEvent = useCaptureEvent();
  const setQueryString = useUpdateQuery();

  // ✅ Generate a consistent query string for API + URL
  const generateQueryString = useCallback(() => {
    const params = [];

    params.push(`page=${pagination.page}`);
    params.push(`per_page=${pagination.perPage}`);
    params.push(`sort=${sort || ""}`);

    for (const key in filters) {
      const item = filters[key];
      if (item?.selectedScope && item?.value) {
        const parsedValue = item.value
          .split(",")
          .map((v) => v.trim())
          .join("|");
        params.push(`filter[${item.selectedScope}]=${parsedValue}`);
      }
    }

    return params.join("&");
  }, [pagination, filters, sort]);

  // ✅ Keep filterQuery and browser URL in sync with all dependencies
  useEffect(() => {
    if (!isHydrated) return;
    const query = generateQueryString();
    setFilterQuery(query);
    setQueryString(query); // keeps browser URL synced
  }, [pagination, filters, sort, isHydrated]);

  // ✅ Refetch on enable + query change
  const query = useQuery(
    [queryKey, filterQuery],
    () =>
      fetchFn({
        query: filterQuery,
        ...restObject,
        options: {
          headers: defaultHeaders,
        },
      }),
    {
      enabled: isHydrated && !!filterQuery && enabled,
      refetchOnWindowFocus: false,
      manual: true,

      retry: (failureCount, error) => {
        if (error?.status === 403 || error?.response?.status === 403) {
          return false;
        }

        return failureCount < 3;
      },
      onSuccess: (response) => {
        setApiResponse(response);
        setData(response.data || []);
        setPaginationInfo(response.pagination || {});
        setFilterMetadata(response.filterMetadata || {});
        setColumnMetadata(response.columnMetadata || {});

        if (getCount) getCount(response?.data?.length);
        if (externalSuccess) externalSuccess(response);
      },
      onError: (error) => {
        console.error(error);
        if (externalError) externalError(error);
      },
    }
  );

  const refetchData = () => query.refetch();

  const getFiltersObject = (data) => {
    setFilters((prevFilters) => {
      const keys = Object.keys(data);
      const changed = keys.some(
        (k) =>
          !prevFilters[k] ||
          prevFilters[k].value !== data[k].value ||
          prevFilters[k].selectedScope !== data[k].selectedScope
      );

      const removedAllFilters = isEmptyObject(data) && !isEmptyObject(prevFilters);

      if (removedAllFilters) {
        setSort("");
      }

      // ✅ If filters changed, reset to page 1
      if (changed || removedAllFilters) {
        setPagination((prev) => ({
          ...prev,
          page: !isHydrated ? initialPagination.page : 1,
        }));
        return data;
      }

      return prevFilters; // no change
    });
  };

  const getPageNumber = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSetSort = (column) => {
    if (sort === `-${column}`) {
      setSort(column);
    } else if (sort === column) {
      setSort(`-${column}`);
    } else {
      setSort(column);
    }
  };

  const getColumns = useCallback((columnConfig) => {
    setColumns(columnConfig);
    const visible = Object.values(columnConfig).filter((col) => col.visible);
    setSortedVisibleColumns(visible.sort((a, b) => a.order - b.order));
  }, []);

 

  useEffect(() => {
    if (hasParams) {
      setFilters(initialFilters);
      setSort(initialSort);

      // ✅ Delay hydration to the next event loop tick to ensure this runs BEFORE the watcher effect
      setTimeout(() => {
        setPagination(initialPagination);
        setIsHydrated(true);
      }, 0);
    } else {
      setIsHydrated(true);
    }
  }, []);

 

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasFilters = Array.from(params.keys()).some((key) => key.startsWith("filter["));
    if (!hasFilters && isHydrated && location.search) {
      const cleaned = location.search.startsWith("?") ? location.search.slice(1) : location.search;
      setFilterQuery(cleaned);
      setQueryString(cleaned);
    }
  }, [location.search, isHydrated]);

  // ✅ Reset table to default state
  const resetTable = useCallback(() => {
    setFilters(defaultFilters);
    setPagination(paginationConfig);
    setSort(defaultSort);
  }, [defaultFilters, defaultSort]);

  return {
    // Core response values
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    apiResponse,

    // States from React Query
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
    isSuccess: query.isSuccess,
    isError: query.isError,
    query,

    // Internal state
    filters,
    sort,
    pagination,
    columns,
    sortedVisibleColumns,

    // Controls
    setFilters,
    setSort,
    setPagination,
    getFiltersObject,
    getPageNumber,
    handleSetSort,
    getColumns,
    refetchData,
    resetTable,
  };
};
